import { pipe, switchMap, tap } from 'rxjs';

import { computed, inject } from '@angular/core';

import { Filter, PaginationFilters } from '@mitsubishi/shared/data-access';
import { ArrayUtils } from '@mitsubishi/utils';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { ProjectOwnerEnum } from '../../enums';
import {
  PaginatedProjects,
  ProjectFilters,
  ProjectSorting,
  ProjectSortingDirection,
  ProjectSortingField,
} from '../../models';
import { ProjectService } from '../../services';

export interface ProjectsListStateModel {
  paginatedData: Partial<PaginatedProjects>;
  currentFilters: ProjectFilters;
  sorting: ProjectSorting;
  pagination: PaginationFilters;
  availableFilters: Filter[];
  isFetching: boolean;
  isLoading: boolean;
  isLoadingFilters: boolean;
}

const initialState: ProjectsListStateModel = {
  currentFilters: {
    ownerFilter: ProjectOwnerEnum.User,
  },
  paginatedData: {},
  availableFilters: [],
  sorting: {
    direction: 'asc',
    field: 'title',
  },
  pagination: {
    total: 0,
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
  },
  isFetching: false,
  isLoading: false,
  isLoadingFilters: false,
};

export const ProjectListStore = signalStore(
  withState(initialState),
  withComputed(({ paginatedData, pagination, sorting }) => ({
    sortedByTitle: computed(() => {
      const projects = paginatedData?.()?.data || [];
      return ArrayUtils.groupByFirstLetter(projects, project => project.title, sorting.direction());
    }),
    paginationHasNextPage: computed(() => {
      return pagination.currentPage() < pagination.totalPages();
    }),
  })),
  withMethods((store, projectService = inject(ProjectService)) => {
    const load = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isFetching: true, isLoading: store.pagination.currentPage() === 1 })),
        switchMap(() =>
          projectService
            .getPaginatedProjectsWithFilters({
              filters: store.currentFilters(),
              orderBy: store.sorting.field(),
              orderDirection: store.sorting.direction(),
              page: store.pagination.currentPage(),
              pageSize: store.pagination.pageSize(),
            })
            .pipe(
              tapResponse({
                next: result => {
                  patchState(store, {
                    paginatedData: {
                      ...result,
                      data: [
                        ...(store.pagination.currentPage() !== 1 ? (store.paginatedData?.()?.data ?? []) : []),
                        ...result.data,
                      ],
                    },
                    pagination: {
                      total: result.total,
                      currentPage: result.page,
                      pageSize: result.pageSize,
                      totalPages: Math.ceil(result.total / result.pageSize),
                    },
                    isFetching: false,
                    isLoading: false,
                  });
                },
                error: () => patchState(store, { isLoading: false, isFetching: false }),
              })
            )
        )
      )
    );

    const loadProjectsFilters = () => {
      patchState(store, { isLoadingFilters: true });
      projectService.getProjectAvailableFilters(store.currentFilters()).subscribe({
        next: filters => {
          patchState(store, { availableFilters: filters, isLoadingFilters: false });
        },
      });
    };

    const updateFilters = () => {
      patchState(store, { pagination: { ...initialState.pagination } });
      load();
      loadProjectsFilters();
    };

    return {
      load,
      loadProjectsFilters,
      updateFilters,
      setOwnerFilter: (value: ProjectOwnerEnum) => {
        patchState(store, { currentFilters: { ...store.currentFilters(), ownerFilter: value } });
        updateFilters();
      },
      setFilter: (fieldId: string, value: any) => {
        const newFilters = { ...store.currentFilters() };
        newFilters[fieldId] = value;
        patchState(store, { currentFilters: newFilters });
        updateFilters();
      },
      setSorting: (field: ProjectSortingField) => {
        patchState(store, { sorting: { ...store.sorting(), field } });
        updateFilters();
      },
      setSortingDirection: (direction: ProjectSortingDirection) => {
        patchState(store, { sorting: { ...store.sorting(), direction } });
        updateFilters();
      },
      setMultipleFilters: (filters: { fieldId: string; value: any }[]) => {
        const newFilters = { ...store.currentFilters() };
        filters.forEach(({ fieldId, value }) => {
          newFilters[fieldId] = value;
        });
        patchState(store, { currentFilters: newFilters });
        updateFilters();
      },
      setCurrentPage: (page: number) => {
        patchState(store, { pagination: { ...store.pagination(), currentPage: page } });
        load();
      },
      setTextSearch: (searchedText: string | null) => {
        patchState(store, {
          currentFilters: { ...store.currentFilters(), text: searchedText ?? undefined },
        });
        updateFilters();
      },
      setDateFilter: (start: string | null, end: string | null) => {
        patchState(store, {
          currentFilters: { ...store.currentFilters(), startDate: start, endDate: end },
        });
        updateFilters();
      },
    };
  })
);
